/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  isEntityInvalid,
  PlainImmutablePageState,
  setEntityField,
  setEntityFields,
} from '@gh/common-shared';
import { DirtyType, ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { first, pick } from 'lodash';
import { Observable } from 'rxjs';
import {
  getReportFyaThresholdId,
  REPORT_FYA_DETAILS_INIT,
  ReportFyaDetails,
  ReportFyaDetailsTab,
  ReportFyaThreshold,
  reportFyaThresholdArr,
  setReportFyaThresholdField,
  validateReportFyaThresholdOverlaps,
} from '../../resources/report-fya-details.model';

@Injectable()
export class ReportFyaDetailsFormState extends PlainImmutablePageState<ReportFyaDetails> {
  selectedTab$ = this.select(({ selectedTab }) => selectedTab);
  thresholds$ = this.select(({ thresholds }) => thresholds);
  focusedCell$ = this.select(({ focusedCell }) => focusedCell);

  constructor() {
    super(REPORT_FYA_DETAILS_INIT, FormMode.EditMode);
  }

  get invalid(): boolean {
    return isNotNil(this.firstInvalidThreshold);
  }

  get firstInvalidThreshold(): Maybe<ReportFyaThreshold> {
    return first(this.value.thresholds.filter(isEntityInvalid));
  }

  get thresholds(): ReportFyaThreshold[] {
    return this.value.thresholds;
  }

  build(details: ReportFyaDetails): void {
    this.setValue(details);
    this.resetDirty();
  }

  merge(details: ReportFyaDetails): void {
    this.setFields(pick(details, 'thresholds', 'keyGoals'));
    this.resetDirty();
  }

  setSelectedTab(tab: ReportFyaDetailsTab): void {
    this.setField('selectedTab', tab);
  }

  setFocusedCell(focusedCell?: FocusedCell): void {
    this.setField('focusedCell', focusedCell);
  }

  setThresholdField(thresholdToUpdate: ReportFyaThreshold, field: string, value: any): void {
    this.setFieldWithDirty(
      'thresholds',
      validateReportFyaThresholdOverlaps(
        reportFyaThresholdArr.update(
          this.value.thresholds,
          getReportFyaThresholdId(thresholdToUpdate),
          (threshold) => setReportFyaThresholdField(threshold, field, value)),
        thresholdToUpdate.type));
  }

  setKeyGoal(officeId: ID, content: string): void {
    this.setKeyGoalField(officeId, 'keyGoals', content);
  }

  setComments(officeId: ID, content: string): void {
    this.setKeyGoalField(officeId, 'comments', content);
  }

  keyGoalForOffice$(id: ID): Observable<string> {
    return this.select(({ keyGoals }) => keyGoals[id]).pipe(map$((bundle) => bundle ? bundle.keyGoals : ''));
  }

  commentsForOffice$(id: ID): Observable<string> {
    return this.select(({ keyGoals }) => keyGoals[id]).pipe(map$((bundle) => bundle ? bundle.comments : ''));
  }

  private setKeyGoalField(officeId: ID, field: string, content: string): void {
    const textBundle = this.value.keyGoals[officeId];
    this.setFieldWithDirty(
      'keyGoals',
      setEntityField(
        this.value.keyGoals,
        officeId,
        setEntityFields(
          textBundle || {},
          {
            officeId,
            [field]: content,
            dirtyType: textBundle ? (textBundle.dirtyType || DirtyType.Updated) : DirtyType.New,
          })));
  }
}
